"use client";

import { signIn, useSession } from "next-auth/react";
import Link from "next/link";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import { FollowCommunityButtonBySlug } from "../community-components/CommunityHeader/FollowCommunityButton";
import { useEffect, useState } from "react";
import { cn } from "~/utils/fontsConfig";
import { Input } from "../themed/Input";
import { z } from "zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../shadcn/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toastHandler } from "~/components/ToastHandler";
import { api } from "~/utils/api";
import { useFollowingStore } from "~/state/store";

export const CommunityCustomBanner = ({
    customBanner,
    communitySlug,
    type = "membership",
    labels,
}: {
    customBanner?: PrismaJson.CustomBanner | null;
    communitySlug: string;
    type?: "membership" | "follower";
    labels?: {
        follow: string;
        unfollow: string;
    };
}) => {
    const { status } = useSession();

    if (!customBanner) return null;
    const data = customBanner as PrismaJson.CustomBanner;
    const [showEmailInput, setShowEmailInput] = useState(false);

    useEffect(() => {
        setShowEmailInput(status === "unauthenticated");
    }, [status]);

    return (
        <div
            className={cn(
                "flex min-h-[170px] w-full flex-col items-center justify-center gap-4 rounded-xl py-8",
                !data.bgColor &&
                    "bg-gradient-to-br from-[#0051EE] to-[#4FE6B0]",
            )}
            style={{
                backgroundColor: data.bgColor,
                color: data.textColor,
            }}
        >
            <h3 className="text-center text-[32px]">{data.heading}</h3>
            <h4 className="max-w-[914px] text-center text-[24px]">
                {data.subheading}
            </h4>
            <div className="mt-2 flex w-full max-w-[350px] flex-col items-center gap-2 md:max-w-[400px]">
                <div className="flex w-full flex-col items-center justify-center gap-2 md:flex-row">
                    <If
                        condition={
                            type === "membership" &&
                            status === "unauthenticated"
                        }
                    >
                        <Button
                            className="min-w-[200px]"
                            variant="default"
                            onClick={() =>
                                signIn(undefined, {
                                    callbackUrl: `/${communitySlug}?follow=true`,
                                }).catch(console.debug)
                            }
                        >
                            Log in
                        </Button>
                    </If>
                    <If condition={type === "follower"}>
                        <div
                            className={cn(
                                "flex w-full items-center justify-center gap-4",
                                showEmailInput && "flex-col",
                            )}
                        >
                            <div
                                className={cn(
                                    "flex items-center gap-2",
                                    showEmailInput && "w-full",
                                )}
                            >
                                <FollowCommunityButtonBySlug
                                    communitySlug={communitySlug}
                                    variant="light-banner"
                                    showEmailInput={showEmailInput}
                                    handleShowInput={() =>
                                        setShowEmailInput(true)
                                    }
                                    labels={labels}
                                />
                            </div>
                            <div className="flex justify-center">
                                {data.cta?.map((c, i) => (
                                    <Link
                                        key={i}
                                        href={c.url}
                                        className="min-w-[200px] md:w-max"
                                    >
                                        <Button
                                            className="w-full"
                                            style={{
                                                backgroundColor:
                                                    data.btnBgColor,
                                                color: data.btnFontColor,
                                            }}
                                            variant="tertiary"
                                        >
                                            {c.name}
                                        </Button>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </If>
                    <If condition={type === "membership"}>
                        <div className="flex justify-center">
                            {data.cta?.map((c, i) => (
                                <Link
                                    key={i}
                                    href={c.url}
                                    className="min-w-[200px] md:w-max"
                                >
                                    <Button
                                        className="w-full"
                                        style={{
                                            backgroundColor: data.btnBgColor,
                                            color: data.btnFontColor,
                                        }}
                                    >
                                        {c.name}
                                    </Button>
                                </Link>
                            ))}
                        </div>
                    </If>
                </div>
                {/* <If condition={isMember}> we do not display this for members anymore
                    <Button
                        onClick={() => setOpen(true)}
                        className="w-full md:w-[200px]"
                        style={{
                            backgroundColor: data.btnBgColor,
                            color: data.btnFontColor,
                        }}
                    >
                        Host an event
                    </Button>
                    <ProposeEventModal
                        community={community}
                        setOpen={setOpen}
                        open={open}
                    />
                </If> */}
            </div>
        </div>
    );
};

const followWithDataSchema = z.object({
    name: z.string().min(5, "Name must be at least 5 characters long"),
    company: z
        .string()
        .min(2, "Company name must be at least 2 characters long"),
    email: z
        .string()
        .regex(
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            "Please enter a valid email address",
        ),
});
type FollowWithData = z.infer<typeof followWithDataSchema>;
export const CommunityCustomBannerUtahTechWeek = ({
    customBanner,
    communityId,
}: {
    customBanner?: PrismaJson.CustomBanner | null;
    communityId: string;
}) => {
    const session = useSession();
    const follow = api.followers.followCommunity.useMutation(undefined);
    const { email: storeEmail, setEmail: setStoreEmail } = useFollowingStore();
    const followersCtx = api.useUtils().followers;

    const form = useForm<FollowWithData>({
        resolver: zodResolver(followWithDataSchema),
        defaultValues: {
            name: undefined,
            company: undefined,
            email: undefined,
        },
        mode: "onChange",
    });
    // name + email if not logged in (or no name!)

    if (!customBanner) return null;
    const data = customBanner as PrismaJson.CustomBanner;
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        setIsLoggedIn(session.status !== "unauthenticated");
        if (session.data) {
            form.reset({
                name: session.data.user.name || undefined,
                email: session.data.user.email || undefined,
            });
            form.trigger(); // trigger validation
        }
    }, [session]);

    const onSubmit = async (data: FollowWithData) => {
        await toastHandler({
            loadingMessage: "Following community...",
            successMessage: "Community followed successfully",
            errorMessage: "Failed to follow community",
            operation: async () => {
                await follow.mutateAsync({
                    communityId,
                    ...data,
                });
                setStoreEmail(data.email);
                followersCtx.invalidate();
            },
        });
    };

    return (
        <div
            className={cn(
                "flex min-h-[170px] w-full flex-col items-center justify-center gap-4 rounded-xl py-8",
                !data.bgColor &&
                    "bg-gradient-to-br from-[#0051EE] to-[#4FE6B0]",
            )}
            style={{
                backgroundColor: data.bgColor,
                color: data.textColor,
            }}
        >
            <h3 className="text-center text-[32px]">{data.heading}</h3>
            <h4 className="max-w-[914px] text-center text-[24px]">
                {data.subheading}
            </h4>
            <div className="mt-2 flex w-full max-w-[450px] flex-col items-center gap-2 md:max-w-[600px]">
                <div className="flex w-full flex-col items-center justify-center gap-2 p-2 md:flex-row">
                    <Form {...form}>
                        <form
                            className="flex w-full flex-col items-center justify-center gap-4 p-2"
                            onSubmit={form.handleSubmit(onSubmit)}
                        >
                            {(!isLoggedIn ||
                                form.getFieldState("name").invalid) && (
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem className="flex w-full flex-col">
                                            <div className="flex items-center gap-4">
                                                <FormLabel className="w-[100px] tracking-wide text-white">
                                                    Name
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className="grow rounded-full tracking-wide"
                                                        placeholder="Enter your name"
                                                    />
                                                </FormControl>
                                            </div>
                                            <FormMessage className="ml-[125px] mt-1 text-sm text-red-200" />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <FormField
                                control={form.control}
                                name="company"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col">
                                        <div className="flex items-center gap-4">
                                            <FormLabel className="w-[100px] tracking-wide text-white">
                                                Company
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    className="grow rounded-full tracking-wide"
                                                    placeholder="Company you're representing"
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage className="ml-[125px] mt-1 text-sm text-red-200" />
                                    </FormItem>
                                )}
                            />

                            {(!isLoggedIn ||
                                form.getFieldState("email").invalid) && (
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem className="flex w-full flex-col">
                                            <div className="flex items-center gap-4">
                                                <FormLabel className="w-[100px] tracking-wide text-white">
                                                    Email
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className="grow rounded-full tracking-wide"
                                                        placeholder="Your email address"
                                                    />
                                                </FormControl>
                                            </div>
                                            <FormMessage className="ml-[125px] mt-1 text-sm text-red-200" />
                                        </FormItem>
                                    )}
                                />
                            )}
                            <div className="flex flex-col items-center justify-center gap-2 md:flex-row">
                                <Button
                                    disabled={!form.formState.isValid}
                                    className="bg-white/10 backdrop-blur-md disabled:bg-opacity-75 disabled:text-white disabled:opacity-90 md:bg-transparent"
                                    variant="tertiary"
                                >
                                    See Calendar
                                </Button>
                                OR
                                <Button
                                    variant="tertiary"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        signIn().catch(console.debug);
                                    }}
                                >
                                    Log in
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </div>
    );
};
